import { Fragment, render } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import aa from 'search-insights';
import { createElement } from '../utils/createElement';

import i18next from 'i18next';
const searchClient = algoliasearch(
  window.global_values.ALGOLIA__APP_ID,
  window.global_values.ALGOLIA__API_SEARCH_KEY,
);

// Algolia Insights：初始化以送出 autocomplete 的 view/click events
// （數據能見度 + 後續 Recommend 地基；使用 search key，事件走獨立 Insights API，不計入 search requests）
aa('init', {
  appId: window.global_values.ALGOLIA__APP_ID,
  apiKey: window.global_values.ALGOLIA__API_SEARCH_KEY,
});

// autocomplete 最小查詢字數：降低 search requests 與 no-results
// CJK-aware：中日文單字即有意義（zh-TW/jp/zh-CN），純拉丁需較多字元才查
const MIN_QUERY_LENGTH = 2;
const PROFILE_MIN_QUERY_LENGTH = 3;
const CJK_REGEX = /[぀-ヿ㐀-鿿豈-﫿]/;
function queryThresholds(query) {
  const hasCJK = CJK_REGEX.test(query);
  return {
    minPost: hasCJK ? 1 : MIN_QUERY_LENGTH,
    minProfile: hasCJK ? MIN_QUERY_LENGTH : PROFILE_MIN_QUERY_LENGTH,
  };
}

function isParentElementHidden(element) {
  var parent = element.parentElement;
  if (parent) {
    const style = window.getComputedStyle(parent);
    return style.display === 'none';
  }
  return false;
}

const SvgIcon = () => (
  <svg width="16" height="16" class="-ml-1 mr-2" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" clipRule="evenodd" d="M10.3697 8.02915C12.1475 8.02915 12.1475 5.59437 12.1475 5.59437C12.1475 5.59437 13.3327 6.81176 13.3327 9.85523C13.3327 12.8987 11.5549 15.3335 8.00086 15.3335C4.44681 15.3335 2.66602 12.8977 2.66602 9.85523C2.66602 5.59437 6.81416 6.20306 6.81416 1.3335C6.81416 1.3335 10.3697 3.15958 10.3697 8.02915Z" stroke="#FFA694" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.55794 14.7955C4.81664 10.4986 7.70738 8 7.70738 8C7.70738 8 7.70738 13.5462 10.6667 11.6975C10.6667 11.6975 10.6667 14.7171 8.8911 15.3333" stroke="#FFA694" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const PVSvgIcon = () => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-0.5">
    <path d="M5.99941 9.57694C8.22979 9.57694 10.0379 7.76885 10.0379 5.53847C10.0379 3.30808 8.22979 1.5 5.99941 1.5C3.76902 1.5 1.96094 3.30808 1.96094 5.53847C1.96094 7.76885 3.76902 9.57694 5.99941 9.57694Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M5.9982 7.15363C6.89035 7.15363 7.61359 6.43039 7.61359 5.53824C7.61359 4.64609 6.89035 3.92285 5.9982 3.92285C5.10605 3.92285 4.38281 4.64609 4.38281 5.53824C4.38281 6.43039 5.10605 7.15363 5.9982 7.15363Z" fill="white"/>
    <path d="M8.15536 11.9996L6.00119 9.8457L3.84766 11.9996" stroke="white" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
  </svg>
)

class AutocompleteBar {
  constructor({ keyword, popularTags, creatorTags, pvTags, container }) {
    this.keyword = keyword;
    this.searchClient = searchClient;
    this.container = container;
    this.autocomplete = null;
    this.popularTags = popularTags.split(',')
    this.creatorTags = creatorTags.split(',')
    this.pvTags = pvTags.split(',')
  }

  init() {
    this.containerElement = document.querySelector(this.container);
    if (!this.containerElement) return;

    const containerElement = document.querySelector(this.container)
    if(!this.autocomplete && !isParentElementHidden(containerElement)) {
      this.createInstance()
    }
  }

  createInstance() {
    const createFooterTemplate = () => {
      return (
        <div className="aa-tags">
          <div className="aa-tags-container pv-tags-container">
            <div className="aa-tags-title">
              {i18next.t('auto_complete_bar.phrozen_verified')}

              <span>
                (95%+)
              </span>
            </div>
            <div className="aa-tags-items">
              {this.pvTags.map((tag, index) => (
                <a key={`pv-tag-${index}-${tag}`} href={`/posts?kw=${tag}`} className="aa-tags-item">
                  <span class='flex text-medium text-xs'>
                    <PVSvgIcon />
                    PV
                  </span>
                  {tag}
                </a>
              ))}
            </div>
          </div>
          <div className="aa-tags-container">
            <div className="aa-tags-title">{i18next.t('auto_complete_bar.popular_tags')}</div>
            <div className="aa-tags-items">
              {this.popularTags.map((tag, index) => (
                <a key={`popular-tag-${index}-${tag}`} href={`/posts?kw=${tag}`} className="aa-tags-item">
                  {index < 5 && <SvgIcon /> }
                  {tag}
                </a>
              ))}
            </div>
          </div>
          <div className="aa-tags-container">
            <div className="aa-tags-title">{i18next.t('auto_complete_bar.popular_designers')}</div>
            <div className="aa-tags-items">
              {this.creatorTags.map((tag, index) => (
                <a key={`creator-tag-${index}-${tag}`} href={`/posts?kw=${tag}`} className="aa-tags-item">
                  <SvgIcon />
                  {tag}
                </a>
              ))}
            </div>
          </div>
        </div>
      );
    };
    this.autocomplete = autocomplete({
      container: `${this.container} .autocomplete`,
      panelContainer: `${this.container} .search-panel`,
      openOnFocus: true,
      debounce: 500, // 避免過於頻繁的搜尋請求，減少競爭條件與 search requests
      insights: { insightsClient: aa }, // Algolia Insights：自動送出 view/click events 並啟用 clickAnalytics
      placeholder: i18next.t('auto_complete_bar.search'),
      initialState: {
        // 不使用空字串的話panel 不會打開
        query: this.keyword || new URL(window.location).searchParams.get('kw') || '',
      },
      getSources: ({ query }) => {
        const footerOnlySource = {
          sourceId: 'footerOnly',
          getItems: () => [],
          templates: {
            footer: createFooterTemplate,
            noResults() {
              return '';
            },
          },
        };

        const { minPost, minProfile } = queryThresholds(query);

        // 查詢過短（含空字串）只顯示熱門 footer，不打 Algolia → 降低 search requests 與 no-results
        if (query.length < minPost) {
          return [footerOnlySource];
        }

        return [
          // Profile 較晚才查（短 query 只查 Post），減少雙 index 的 search requests
          ...(query.length >= minProfile ? [{
            sourceId: 'profiles',
            getItems: () => {
              const results = getAlgoliaResults({
                searchClient: this.searchClient,
                queries: [
                  {
                    indexName: 'Profile',
                    query,
                    params: {
                      hitsPerPage: 2,
                      clickAnalytics: true,
                      // 打字中的查詢刻意仍然記帳，但標成 autocomplete：熱門關鍵字改抓
                      // tags=result_page，前綴碎片（poke、pokem）因此不會混進清單
                      analyticsTags: ['autocomplete'],
                    },
                  },
                ],
              });
              return results
            },
            onSelect: (event) => {
              event.setQuery(event.item.brand_name);
              if(window.trackingEvent) window.trackingEvent.search({ search_term: event.item.brand_name })
              window.location.href = `/posts?kw=${event.item.brand_name}`
            },
            templates: {
              item: ({ item, components }) => {
                return (
                  <div className="aa-ItemWrapper">
                    <div className="aa-ItemContent">
                      <div className="aa-ItemContentBody">
                        <div className="aa-ItemContentTitle">
                          <div class="item-name"><components.Snippet hit={item} attribute="brand_name"/></div>
                          <div class="creator-tag">
                            { i18next.t('auto_complete_bar.creator') }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              },
            },
          }] : []),
          {
            sourceId: 'posts',
            getItems: () => {
              const results = getAlgoliaResults({
                searchClient: this.searchClient,
                queries: [
                  {
                    indexName: 'Post',
                    query,
                    params: {
                      hitsPerPage: 4,
                      clickAnalytics: true,
                      analyticsTags: ['autocomplete'], // 同上：與結果頁的搜尋分流
                    },
                  },
                ],
              });
              return results
            },
            onSelect: (event) => {
              event.setQuery(event.item.title);
              if(window.trackingEvent) window.trackingEvent.search({ search_term: event.item.title })
              window.location.href = `/posts?kw=${event.item.title}`
            },
            templates: {
              item: ({ item, components }) => {
                return (
                  <div className="aa-ItemWrapper">
                    <div className="aa-ItemContent">
                      <div className="aa-ItemContentBody">
                        <div className="aa-ItemContentTitle">
                          <components.Snippet hit={item} attribute="title"/>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              },
              footer: createFooterTemplate,
              noResults() {
                // create .aa-SourceNoResults div
                return '';
              },
            },
          },
        ];
      },
      onSubmit({ state }) {
        if (state.query !== '') {
          if(window.trackingEvent) window.trackingEvent.search({ search_term: state.query })
          window.location.href = `/posts?kw=${state.query}`
        } else {
          window.location.href = '/posts'
        }
      },
      renderer: { createElement: createElement, Fragment, render },
    })

    // add blur event listener to input box
    const inputElement = this.containerElement.querySelector('.aa-Input');
    if (inputElement) {
      inputElement.addEventListener('blur', () => {
        setTimeout(() => {
          const activeElement = document.activeElement;
          const panel = this.containerElement.querySelector('.aa-Panel');

          if (panel && !panel.contains(activeElement) && this.autocomplete) {
            this.autocomplete.setIsOpen(false);
          }
        }, 100);
      });
    }
    // hidden mobile-search-skeleton
    const mobileSearchbar = this.containerElement.closest('.mobile-searchbar');
    if (mobileSearchbar) {
      const skeleton = mobileSearchbar.querySelector('.mobile-search-skeleton');
      if (skeleton ) {
        skeleton.classList.add('hidden');
      }
    }
  }
}

export default AutocompleteBar
